import { useState, type FormEvent } from 'react';
import { MIN_PASSWORD_LENGTH } from '../lib/password';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ApiError, apiRequest } from '../lib/api';
import { usePortal } from '../lib/portal';
import { Alert, Button, Field, PasswordInput } from '../components/ui';
import '../styles/auth.css';

import { EnvBadge } from '../components/EnvBadge';
/**
 * Turning an invitation into a password.
 *
 * This screen was missing entirely, which meant the server could issue an
 * invitation and the web app had nowhere to spend it. It affected staff
 * invitations from Settings › Users as much as the tenant ones added with the
 * portal — both produced a token and a dead end.
 *
 * One page serves both, because from here they are the same act: somebody
 * holding a token chooses a password and becomes able to sign in. Who they turn
 * out to be is decided by the roles already attached to their invitation, and
 * the sign-in page then routes them accordingly. Nothing here needs to know
 * which kind of person is typing.
 *
 * It does NOT sign them in afterwards. acceptInvitation returns an identity,
 * not a session, and minting one here would mean a second path to being logged
 * in — the thing this module has avoided everywhere else.
 */
export function AcceptInvitation() {
  const { siteHome } = usePortal();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    // Checked here as well as by the server: a mistyped confirmation is the
    // commonest failure on this form, and a round trip to learn it is a poor
    // way to be told.
    if (password !== confirm) {
      setError('Those two passwords do not match.');
      return;
    }
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }

    setBusy(true);
    try {
      await apiRequest('/api/v1/auth/invitations/accept', {
        method: 'POST', auth: false, body: { token, password },
      });
      setDone(true);
      // Long enough to read the confirmation, short enough not to be a wait.
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_token') {
        setError('This invitation has expired or has already been used. Ask for a new one.');
      } else if (err instanceof ApiError && err.code === 'network') {
        setError('Setting your password needs a connection. Check your network and try again.');
      } else {
        setError(err instanceof ApiError ? err.message : 'That did not work. Please try again.');
      }
      setBusy(false);
    }
  };

  return (
    <div className="auth-wrap">
      <div className="auth-brand-row">
        <Link to={siteHome} className="auth-brand">NyumbaSuite</Link>
        <EnvBadge />
      </div>
      <form className="auth-card login" onSubmit={submit} noValidate>
        <h1 className="auth-title">Set your password</h1>

        {done ? (
          <>
            <Alert tone="success">
              Your password is set. Taking you to sign in…
            </Alert>
            <div className="auth-foot"><Link to="/login">Sign in now</Link></div>
          </>
        ) : !token ? (
          /* Reached without a token — usually a link that lost its query string
             on the way through a mail client. Say so plainly rather than
             showing a form that cannot possibly work. */
          <>
            <Alert tone="warning">
              This link is missing its invitation code. Open the link from your
              invitation email exactly as it was sent, or ask for a new one.
            </Alert>
            <div className="auth-foot"><Link to="/login">Back to sign in</Link></div>
          </>
        ) : (
          <>
            <p className="auth-lede">
              Choose a password and you are in. You will sign in with the email
              address your invitation was sent to.
            </p>

            {error && <div style={{ marginBottom: 16 }}><Alert tone="danger">{error}</Alert></div>}

            <Field label="New password" htmlFor="password" hint={`At least ${MIN_PASSWORD_LENGTH} characters.`}>
              <PasswordInput id="password" autoComplete="new-password" autoFocus
                value={password} onChange={(e) => setPassword(e.target.value)} required />
            </Field>

            <Field label="Confirm password" htmlFor="confirm">
              <PasswordInput id="confirm" autoComplete="new-password"
                value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </Field>

            <Button type="submit" block loading={busy}>Set password</Button>

            <div className="auth-foot">
              Already set it up? <Link to="/login">Sign in</Link>
            </div>
          </>
        )}
      </form>
      <Link className="auth-link" to={siteHome}>← Back to homepage</Link>
    </div>
  );
}
